import { useState, useCallback, useRef } from 'react';

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
  isInRange: boolean;
  /** True when mode is 'single' and this day is the selected date */
  isSelected: boolean;
  /** True when day is outside minDate/maxDate */
  isDisabled: boolean;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isInRange(date: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false;
  const t = date.getTime();
  return t > start.getTime() && t < end.getTime();
}

function isDateDisabled(date: Date, minDate?: Date | null, maxDate?: Date | null): boolean {
  const t = date.getTime();
  if (minDate != null) {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    if (t < min.getTime()) return true;
  }
  if (maxDate != null) {
    const max = new Date(maxDate);
    max.setHours(23, 59, 59, 999);
    if (t > max.getTime()) return true;
  }
  return false;
}

function getDaysInMonth(
  year: number,
  month: number,
  rangeStart: Date | null,
  rangeEnd: Date | null,
  opts: {
    selectedDate?: Date | null;
    minDate?: Date | null;
    maxDate?: Date | null;
  } = {},
): CalendarDay[] {
  const { selectedDate = null, minDate, maxDate } = opts;
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = first.getDay();
  const daysInMonth = last.getDate();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days: CalendarDay[] = [];
  const totalCells = Math.ceil((startPad + daysInMonth) / 7) * 7;

  for (let i = 0; i < totalCells; i++) {
    const cellIndex = i - startPad;
    let date: Date;
    let isCurrentMonth: boolean;

    if (cellIndex < 0) {
      const prevMonth = new Date(year, month, 0);
      date = new Date(year, month - 1, prevMonth.getDate() + cellIndex + 1);
      isCurrentMonth = false;
    } else if (cellIndex >= daysInMonth) {
      date = new Date(year, month + 1, cellIndex - daysInMonth + 1);
      isCurrentMonth = false;
    } else {
      date = new Date(year, month, cellIndex + 1);
      isCurrentMonth = true;
    }

    date.setHours(0, 0, 0, 0);

    const disabled = isDateDisabled(date, minDate, maxDate);
    const selected = selectedDate ? isSameDay(date, selectedDate) : false;

    days.push({
      date,
      isCurrentMonth,
      isToday: isSameDay(date, today),
      isRangeStart: rangeStart ? isSameDay(date, rangeStart) : false,
      isRangeEnd: rangeEnd ? isSameDay(date, rangeEnd) : false,
      isInRange: isInRange(date, rangeStart, rangeEnd),
      isSelected: selected,
      isDisabled: disabled,
    });
  }

  return days;
}

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export type UseDatePickerOptions =
  | {
      mode?: 'range';
      value?: { start: string | null; end: string | null };
      onChange?: (range: { start: string | null; end: string | null }) => void;
      minDate?: Date | null;
      maxDate?: Date | null;
    }
  | {
      mode: 'single';
      value?: string | null;
      onChange?: (date: string | null) => void;
      minDate?: Date | null;
      maxDate?: Date | null;
    };

function parseOptionalDate(s: string | null | undefined): Date | null {
  if (s == null || s === '') return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format date as YYYY-MM-DD in local time (not UTC) to avoid timezone shifts */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function useDatePicker(options: UseDatePickerOptions = {}) {
  const mode = options.mode ?? 'range';
  const minDate = 'minDate' in options ? options.minDate : undefined;
  const maxDate = 'maxDate' in options ? options.maxDate : undefined;
  const now = new Date();

  const [internalStart, setInternalStart] = useState<Date | null>(null);
  const [internalEnd, setInternalEnd] = useState<Date | null>(null);
  const [internalSingle, setInternalSingle] = useState<Date | null>(null);

  // Refs to track current values for callbacks
  const rangeStartRef = useRef<Date | null>(null);
  const rangeEndRef = useRef<Date | null>(null);

  let rangeStart: Date | null;
  let rangeEnd: Date | null;
  let isControlled: boolean;

  if (mode === 'single') {
    const value = (options as { value?: string | null }).value;
    const singleValue = value != null && value !== '' ? parseOptionalDate(value) : null;
    isControlled = value !== undefined;
    rangeStart = isControlled ? (singleValue ?? null) : internalSingle;
    rangeEnd = null;
  } else {
    const value = (options as { value?: { start: string | null; end: string | null } }).value;
    const valueStart = value?.start != null ? parseOptionalDate(value.start) : undefined;
    const valueEnd = value?.end != null ? parseOptionalDate(value.end) : undefined;
    isControlled = value !== undefined;
    rangeStart = isControlled ? (valueStart ?? null) : internalStart;
    rangeEnd = isControlled ? (valueEnd ?? null) : internalEnd;
  }

  // Keep refs in sync with current values
  rangeStartRef.current = rangeStart;
  rangeEndRef.current = rangeEnd;

  const setRangeStart = useCallback(
    (d: Date | null) => {
      if (mode === 'single') {
        if (!isControlled) setInternalSingle(d);
        const dateStr = d ? formatDateLocal(d) : null;
        (options as { onChange?: (date: string | null) => void }).onChange?.(dateStr);
      } else {
        if (!isControlled) setInternalStart(d);
        const startStr = d ? formatDateLocal(d) : null;
        const endStr = rangeEndRef.current ? formatDateLocal(rangeEndRef.current) : null;
        (options as { onChange?: (r: { start: string | null; end: string | null }) => void }).onChange?.({ start: startStr, end: endStr });
      }
    },
    [mode, isControlled, options],
  );

  const setRangeEnd = useCallback(
    (d: Date | null) => {
      if (mode === 'single') return;
      if (!isControlled) setInternalEnd(d);
      const startStr = rangeStartRef.current ? formatDateLocal(rangeStartRef.current) : null;
      const endStr = d ? formatDateLocal(d) : null;
      (options as { onChange?: (r: { start: string | null; end: string | null }) => void }).onChange?.({ start: startStr, end: endStr });
    },
    [mode, isControlled, options],
  );

  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());

  const days = getDaysInMonth(currentYear, currentMonth, rangeStart, rangeEnd, {
    selectedDate: mode === 'single' ? rangeStart : null,
    minDate,
    maxDate,
  });

  const prevMonth = useCallback(() => {
    setCurrentMonth((m) => {
      if (m === 0) {
        setCurrentYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setCurrentMonth((m) => {
      if (m === 11) {
        setCurrentYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const jumpToYear = useCallback((year: number) => {
    setCurrentYear(year);
  }, []);

  const jumpToMonth = useCallback((month: number) => {
    setCurrentMonth(month);
  }, []);

  const selectDate = useCallback(
    (date: Date) => {
      const d = new Date(date.getTime());
      const disabled = isDateDisabled(d, minDate, maxDate);
      if (disabled) return;

      const currentStart = rangeStartRef.current;
      const currentEnd = rangeEndRef.current;

      if (mode === 'single') {
        if (currentStart && isSameDay(d, currentStart)) {
          if (!isControlled) setInternalSingle(null);
          (options as { onChange?: (date: string | null) => void }).onChange?.(null);
        } else {
          setRangeStart(d);
        }
        return;
      }

      if (!currentStart || (currentStart && currentEnd)) {
        if (!isControlled) {
          setInternalStart(d);
          setInternalEnd(null);
        } else {
          (options as { onChange?: (r: { start: string | null; end: string | null }) => void }).onChange?.({ start: formatDateLocal(d), end: null });
        }
      } else {
        if (d.getTime() < currentStart.getTime()) {
          setRangeEnd(currentStart);
          setRangeStart(d);
        } else if (isSameDay(d, currentStart)) {
          if (!isControlled) {
            setInternalStart(null);
            setInternalEnd(null);
          }
          (options as { onChange?: (r: { start: string | null; end: string | null }) => void }).onChange?.({ start: null, end: null });
        } else {
          setRangeEnd(d);
        }
      }
    },
    [mode, isControlled, minDate, maxDate, options, setRangeStart, setRangeEnd],
  );

  return {
    state: {
      currentYear,
      currentMonth,
      days,
      rangeStart,
      rangeEnd,
      mode,
    },
    actions: {
      prevMonth,
      nextMonth,
      selectDate,
      jumpToYear,
      jumpToMonth,
    },
  };
}
